import React from "react";

/**
 * The DT6 Standard Seal — circular, 13-star ring, "DT6" center, rim text
 * "AMERICA FIRST · AMERICA ONLY · MMXXVI".  The 13-star ring is RESERVED for
 * this seal only — never used as decoration elsewhere per brand rules.
 *
 * Props:
 *   size    — pixel size (default 200)
 *   variant — "primary" (Glory Red on parchment), "mono-parchment" (all parchment,
 *             for email footers), "mono-dark" (all near-black, for light bg).
 *   testId  — optional data-testid override
 */
export default function StandardSeal({ size = 200, variant = "primary", testId = "standard-seal" }) {
  const stroke =
    variant === "mono-parchment" ? "#EDE8DC" :
    variant === "mono-dark" ? "#0B0E14" :
    "#B22234";
  const fill =
    variant === "mono-parchment" ? "#EDE8DC" :
    variant === "mono-dark" ? "#0B0E14" :
    "#EDE8DC";
  const centerColor =
    variant === "mono-parchment" ? "#EDE8DC" :
    variant === "mono-dark" ? "#0B0E14" :
    "#B22234";

  // 13 stars evenly spaced around a ring at radius=72.
  const stars = Array.from({ length: 13 }, (_, i) => {
    const angle = (i / 13) * 2 * Math.PI - Math.PI / 2; // start at top
    const r = 72;
    const cx = 100 + r * Math.cos(angle);
    const cy = 100 + r * Math.sin(angle);
    return { cx, cy };
  });

  // Rim text follows a circular path with radius=88.
  const rimId = `dt6-seal-rim-${size}-${variant}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="The DT6 Standard — America First. America Only."
      data-testid={testId}
    >
      {/* Outer ring */}
      <circle cx="100" cy="100" r="96" fill="none" stroke={stroke} strokeWidth="1.5" />
      {/* Rim ring bands (thin engraved feel) */}
      <circle cx="100" cy="100" r="82" fill="none" stroke={stroke} strokeWidth="0.75" opacity="0.6" />
      <circle cx="100" cy="100" r="60" fill="none" stroke={stroke} strokeWidth="0.75" opacity="0.4" />

      {/* Rim text path (invisible arc the text hugs) */}
      <defs>
        <path id={rimId} d="M 100,100 m -88,0 a 88,88 0 1,1 176,0 a 88,88 0 1,1 -176,0" fill="none" />
      </defs>
      <text
        fontFamily="'JetBrains Mono', 'Courier New', monospace"
        fontSize="11"
        letterSpacing="4"
        fill={stroke}
        textLength="500"
      >
        <textPath href={`#${rimId}`} startOffset="0">
          AMERICA FIRST · AMERICA ONLY · MMXXVI · AMERICA FIRST · AMERICA ONLY · MMXXVI
        </textPath>
      </text>

      {/* 13-star ring */}
      {stars.map((s, i) => (
        <path
          key={i}
          transform={`translate(${s.cx - 6},${s.cy - 6}) scale(0.5)`}
          fill={stroke}
          d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1z"
        />
      ))}

      {/* Center: DT6 wordmark */}
      <text
        x="100"
        y="108"
        textAnchor="middle"
        fontFamily="'Chivo', 'Helvetica Neue', Helvetica, Arial, sans-serif"
        fontSize="34"
        fontWeight="900"
        fill={centerColor}
        letterSpacing="-1"
      >
        DT6
      </text>
    </svg>
  );
}
